<template>
	<TrailerModalQTV
		v-if="isTrailerModalQTVActive && trailerModalQtvShortName && !isBackdropVideoSR"
		:clips="clips"
		:title="title"
		:shortName="trailerModalQtvShortName"
		:source="source"
		:trackingProperty="trackingProperty"
		:additionalContexts="additionalContexts"
		:playerAdditionalContexts="playerAdditionalContexts"
		:hideTrailerAd="hideTrailerAd"
		@closed="handleCloseModal"
		@dismiss="$emit('dismiss')"
	/>
	<NewDesignModal
		v-else
		class="trailer-modal"
		:title="titleName"
		@closed="handleCloseModal"
		@dismiss="$emit('dismiss')"
	>
		<template v-if="showTrailer">
			<div class="native-ad-wrapper" v-if="showNativeAd">
				<NativeAdBackdrop
					:title="title"
					@adCompleted="onNativeBackdropAdComplete"
					@adFailed="onNativeBackdropAdComplete"
				/>
			</div>
			<div class="player-container" v-else>
				<component
					v-if="trailerClip"
					:is="videoPlayer"
					:additionalContexts="playerAdditionalContexts"
					class="player"
					:class="{ 'youtube-player-active': isYoutubePlayer }"
					height="100%"
					:clip="trailerClip"
					:options="options"
					:isBackdrop="false"
					:isActive="true"
					:title="title"
					:hideTrailerAd="hideTrailerAd"
					@onBuffering="onTrack('buffering')"
					@onPlaying="onTrack('playing')"
					@onPaused="onTrack('paused')"
					@onEnded="onTrack('ended')"
				>
				</component>
				<JWTVProFooter v-if="showJWTVProFooter" :title="title" />
			</div>
		</template>
		<div class="sponsored-wrapper" v-if="showSR || !!sponsoredAd">
			<SponsoredRecommendation
				:sponsoredAd="sponsoredAd"
				placement="sr_trailer_backdrop"
				:lazy="false"
				@onVideoEnded="handleTrailerVideoEnded"
				:sraPlacement="sraPlacement"
			/>
		</div>
	</NewDesignModal>
</template>

<script setup lang="ts">
import type { YoutubePlayerOptions } from '@/interfaces/components/youtube-player';
import type { SnowplowContext } from '@/helpers/tracking/providers';
import { toValue, type MaybeRefOrGetter } from 'vue';

import NewDesignModal from '@/components/modals/vfm/NewDesignModal.vue';
import TrailerModalQTV from '@/components/TrailerModalQTV.vue';
import { VFMHelper } from '@/helpers/vfm/vfm-helper';
import { computed, ref, watch } from 'vue';
import type { TrailerClip } from '@/interfaces/trailerClip';
import { getTrailerComponent } from '@/helpers/trailer-helper';
import { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';
import SponsoredRecommendation from '@/features/sponsored-recommendations/components/SponsoredRecommendation.vue';
import { SraEventAction, SraPlacement } from '@/@types/graphql-types';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { useBackendTracking } from '@/helpers/composables/useBackendTracking';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { useTitleDetailInfo } from '@/pages/titleDetailV1/v1/composables/useTitleDetailInfo';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { storeToRefs } from 'pinia';
import { isProWebCountry } from '@/helpers/pro-web-helper';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useNativeAds } from '@/features/native-ads/composables/useNativeAds';
import adminConfig from '@/features/promotions/config.json';

const JWTVProFooter = defineAsyncComponent(() => import('@/pages/titleDetailV1/v1/hero/JWTVProFooter.vue'));
const NativeAdBackdrop = defineAsyncComponent(() => import('@/components/NativeAdBackdrop.vue'));

const props = withDefaults(
	defineProps<{
		clips: TrailerClip[];
		title: TitleDetail;
		source?: string;
		trackingProperty?: string;
		additionalContexts?: SnowplowContext[];
		playerAdditionalContexts?: SnowplowContext[];
		sponsoredAd?: SponsoredAdFragment | null;
		sraPlacement?: SraPlacement;
		hideTrailerAd?: boolean;
	}>(),
	{
		source: 'modal',
		additionalContexts: () => [],
	}
);

// For this placement, we play the SR video once, then display the trailer
const hasSrVideoPlayedOnce = ref(false);

// For NativeAd: show ad before trailer if not SR
const hasNativeAdPlayedOnce = ref(false);

const options: YoutubePlayerOptions = { autoplay: 1, mute: 1 };

const { activeRoute } = storeToRefs(useRoutingStore());
const { trackSraEvent } = useBackendTracking();
const { isPremium } = storeToRefs(useUserStore());
const { titleId, titleName, titleObjectType } = useTitleDetailInfo(() => props.title);
const { webLocale, country } = storeToRefs(useLanguageStore());

const trailerModalQtvShortName = computed(() => {
	const config = adminConfig?.steering_overrides_us;
	if (!config?.trailer_modal_qtv || !config.trailer_modal_provider || country.value !== 'US') {
		return null;
	}
	return config.trailer_modal_provider;
});

const isTrailerModalQTVActive = computed(
	() =>
		trailerModalQtvShortName.value !== null &&
		!props.title.offers.some(offer => offer.package.shortName === trailerModalQtvShortName.value)
);

// Native ad eligibility check
const { shouldShowNativeAd: isNativeAdActive } = useNativeAds({ title: () => props.title, slot: 'BACKDROP' });

watch(
	() => activeRoute,
	() => VFMHelper.close()
);

const isBackdropVideoSR = computed(() => {
	return props.sponsoredAd?.campaign && props.sraPlacement === SraPlacement.BackdropVideo;
});

const showSR = computed(() => isBackdropVideoSR.value && !hasSrVideoPlayedOnce.value);
const showNativeAd = computed(() => !isBackdropVideoSR.value && !hasNativeAdPlayedOnce.value && isNativeAdActive.value);
const showTrailer = computed(() => !showSR.value || props.sponsoredAd?.holdoutGroup === true);

const trailerClip = computed(() => props.clips[0]);

const showJWTVProFooter = computed(() => {
	return !isPremium.value && isProWebCountry(webLocale.value) && trailerClip.value?.provider === 'JUSTWATCH_TV';
});

const isYoutubePlayer = computed(() => {
	return trailerClip.value?.provider === 'YOUTUBE';
});

const videoPlayer = computed(() => {
	return getTrailerComponent(trailerClip.value?.provider);
});

// Once SR videos played once, we can show the trailer
function handleTrailerVideoEnded(hasVideoEnded: boolean) {
	hasSrVideoPlayedOnce.value = hasVideoEnded;
}

function onNativeBackdropAdComplete() {
	hasNativeAdPlayedOnce.value = true;
}

function handleCloseModal() {
	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'trailer_modal_closed',
			label: titleId.value,
			property: 'title-header',
		},
		props.additionalContexts
	);

	// If there's a sponsored ad and the video has not been played once, we track the event
	if (props.sponsoredAd?.campaign && !hasSrVideoPlayedOnce.value) {
		trackSraEvent({
			action: SraEventAction.TrailerModalClosed,
			sponsoredAd: () => props.sponsoredAd ?? undefined,
		});
	}
}

function onTrack(eventName: string) {
	if (trailerClip.value?.provider !== 'YOUTUBE') {
		return;
	}
	const trackingMap = {
		buffering: 'youtube_buffering',
		playing: 'youtube_playing',
		paused: 'youtube_paused',
		ended: 'youtube_ended',
	};
	const category = trackingMap[eventName as keyof typeof trackingMap];
	if (category) {
		TrackingHelper.trackEvent(
			category,
			{
				action: titleObjectType.value.toLowerCase(),
				label: props.source,
				property: props.trackingProperty,
				nonInteraction: true,
				value: 1,
			},
			props.additionalContexts
		);
	}
}
</script>

<style scoped lang="scss">
:deep(.trailer-modal.jw-modal-new) {
	min-height: 730px;
}

.native-ad-wrapper {
	height: 100%;
	width: 100%;
	display: flex;
	background: rgba(0, 0, 0, 0.95);
}

.player-container {
	height: 100%;
	width: 100%;
	display: flex;
	justify-content: center;
	align-items: center;
	flex-direction: column;
}
.player {
	height: 100%;
	width: 100%;
	padding: 20px;
	max-height: 100% !important;
	:deep(.video-player__video-container) {
		height: 100%;
	}
}
.sponsored-wrapper {
	text-align: initial;

	:deep(.backdrop-placement) {
		margin: 0 auto;
		width: 100%;
		max-width: 69%;

		@container (max-width: 640px) {
			max-width: 100%;
		}
	}

	:deep(.variant-control) {
		margin: 0 auto;
		width: 100%;
		max-width: 80%;

		@container (max-width: 640px) {
			max-width: 100%;
		}
	}
}
</style>
